// Skills catalog configuration
// Categories and ordering for the Skills page

export const SKILL_CATEGORIES = [
  {
    id: 'core',
    label: 'Core',
    description: 'The foundation — CellCog core, projects, coding, and co-work',
  },
  {
    id: 'media',
    label: 'Media Production',
    description: 'Video, image, audio, music, and podcast creation',
  },
  {
    id: 'research',
    label: 'Research & Analysis',
    description: 'Deep research, financial analysis, and data science',
  },
  {
    id: 'documents',
    label: 'Documents & Presentations',
    description: 'PDFs, slides, spreadsheets, resumes, and legal docs',
  },
  {
    id: 'apps',
    label: 'Apps & Visualization',
    description: 'Dashboards, prototypes, games, and 3D models',
  },
  {
    id: 'creative',
    label: 'Creative',
    description: 'Branding, comics, storytelling, and social media',
  },
  {
    id: 'planning',
    label: 'Planning & Learning',
    description: 'Thinking partner, education, and travel planning',
  },
  {
    id: 'integrations',
    label: 'Integrations',
    description: 'Partner model integrations and extensions',
  },
];

// Skills that should show a "Beta" badge
export const BETA_SKILLS = new Set(['pair-programming-cellcog', 'coding-agent-cellcog']);

// Map skill slugs to categories
export const SKILL_CATEGORY_MAP = {
  'cellcog': 'core',
  'pair-programming-cellcog': 'core',
  'project-management-cellcog': 'core',
  'coding-agent-cellcog': 'core',
  'deep-research-cellcog': 'research',
  'stock-analysis-cellcog': 'research',
  'crypto-research-cellcog': 'research',
  'data-analysis-cellcog': 'research',
  'news-briefing-cellcog': 'research',
  'video-generation-cellcog': 'media',
  'cinematic-video-cellcog': 'media',
  'image-generation-cellcog': 'media',
  'audio-generation-cellcog': 'media',
  'music-generation-cellcog': 'media',
  'podcast-generation-cellcog': 'media',
  'meme-generator-cellcog': 'media',
  'nano-banana-image-cellcog': 'media',
  'seedance-video-generation-cellcog': 'media',
  'pdf-document-generation-cellcog': 'documents',
  'presentation-slides-cellcog': 'documents',
  'excel-spreadsheet-cellcog': 'documents',
  'resume-cover-letter-cellcog': 'documents',
  'legal-documents-cellcog': 'documents',
  'dashboard-web-app-cellcog': 'apps',
  'ui-prototype-wireframe-cellcog': 'apps',
  'game-asset-generation-cellcog': 'apps',
  '3d-model-generation-cellcog': 'apps',
  'logo-brand-identity-cellcog': 'creative',
  'comic-manga-generator-cellcog': 'creative',
  'creative-writing-cellcog': 'creative',
  'instagram-reels-tiktok-cellcog': 'creative',
  'youtube-video-cellcog': 'creative',
  'brainstorming-strategy-cellcog': 'planning',
  'tutoring-education-cellcog': 'planning',
  'travel-planning-cellcog': 'planning',
  'gif-generator-cellcog': 'media',
  'sticker-generator-cellcog': 'media',
  'diagram-flowchart-cellcog': 'apps',
};

// Legacy pre-migration slugs -> current slugs (2026-07 ClawHub slug migration).
// Old /skills/<slug> links must keep resolving; SkillDetailPage redirects using this map.
export const LEGACY_SKILL_SLUGS = {
  'image-cog': 'image-generation-cellcog',
  'video-cog': 'video-generation-cellcog',
  'research-cog': 'deep-research-cellcog',
  'fin-cog': 'stock-analysis-cellcog',
  'spreadsheets-cog': 'excel-spreadsheet-cellcog',
  'audio-cog': 'audio-generation-cellcog',
  'music-cog': 'music-generation-cellcog',
  'pod-cog': 'podcast-generation-cellcog',
  'docs-cog': 'pdf-document-generation-cellcog',
  'slides-cog': 'presentation-slides-cellcog',
  'resume-cog': 'resume-cover-letter-cellcog',
  'legal-cog': 'legal-documents-cellcog',
  'data-cog': 'data-analysis-cellcog',
  'dash-cog': 'dashboard-web-app-cellcog',
  'diagram-cog': 'diagram-flowchart-cellcog',
  '3d-cog': '3d-model-generation-cellcog',
  'meme-cog': 'meme-generator-cellcog',
  'gif-cog': 'gif-generator-cellcog',
  'sticker-cog': 'sticker-generator-cellcog',
  'comi-cog': 'comic-manga-generator-cellcog',
  'game-cog': 'game-asset-generation-cellcog',
  'proto-cog': 'ui-prototype-wireframe-cellcog',
  'brand-cog': 'logo-brand-identity-cellcog',
  'banana-cog': 'nano-banana-image-cellcog',
  'nano-banana-cog': 'nano-banana-image-cellcog',
  'seedance-cog': 'seedance-video-generation-cellcog',
  'cine-cog': 'cinematic-video-cellcog',
  'insta-cog': 'instagram-reels-tiktok-cellcog',
  'tube-cog': 'youtube-video-cellcog',
  'crypto-cog': 'crypto-research-cellcog',
  'news-cog': 'news-briefing-cellcog',
  'learn-cog': 'tutoring-education-cellcog',
  'story-cog': 'creative-writing-cellcog',
  'think-cog': 'brainstorming-strategy-cellcog',
  'travel-cog': 'travel-planning-cellcog',
  'avatar-cog': 'avatar-creation-cellcog',
  'code-cog': 'coding-agent-cellcog',
  'cowork-cog': 'pair-programming-cellcog',
  'project-cog': 'project-management-cellcog',
};

// Words that need special casing when formatting slugs for display
const DISPLAY_WORD_OVERRIDES = {
  '3d': '3D',
  'ai': 'AI',
  'ui': 'UI',
  'pdf': 'PDF',
  'gif': 'GIF',
  'youtube': 'YouTube',
  'tiktok': 'TikTok',
};

// Format skill name for display (matches ClawHub naming convention)
// image-generation-cellcog -> Image Generation — CellCog
// cellcog -> CellCog, 3d-model-generation-cellcog -> 3D Model Generation — CellCog
// { brand: false } drops the "— CellCog" suffix: the in-app Delegate > Skills surfaces only (Nitish, Sep 23 2026).
// The public /skills page keeps the default (brand: true).
export const formatSkillDisplayName = (name, { brand = true } = {}) => {
  if (!name) return '';
  if (name === 'cellcog') return 'CellCog';

  // New-formula slugs end with the brand token; render it as a "— CellCog" suffix
  const hasBrandSuffix = name.endsWith('-cellcog');
  const base = hasBrandSuffix ? name.slice(0, -'-cellcog'.length) : name;

  const formatted = base
    .split('-')
    .map(word => {
      const override = DISPLAY_WORD_OVERRIDES[word.toLowerCase()];
      if (override) return override;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');

  return hasBrandSuffix && brand ? `${formatted} — CellCog` : formatted;
};

// Get category for a skill slug
export const getSkillCategory = (slug) => {
  return SKILL_CATEGORY_MAP[slug] || 'integrations';
};

// Group skills by category
export const groupSkillsByCategory = (skills) => {
  const grouped = {};

  // Initialize all categories
  SKILL_CATEGORIES.forEach(cat => {
    grouped[cat.id] = {
      ...cat,
      skills: [],
    };
  });

  // Assign skills to categories
  skills.forEach(skill => {
    const categoryId = getSkillCategory(skill.slug);
    if (grouped[categoryId]) {
      grouped[categoryId].skills.push(skill);
    }
  });

  // Return as ordered array, filtering out empty categories
  return SKILL_CATEGORIES
    .map(cat => grouped[cat.id])
    .filter(group => group.skills.length > 0);
};
